import { mdiCheck, mdiEye, mdiEyeOff } from "@mdi/js";
import { useId, useState, type ReactNode } from "react";
import { RegReqLabel } from "./RegReqLabel";

type Props = {
  label: ReactNode;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  autoComplete?: "new-password" | "current-password";
  error?: string;
  /** When set, shows a green check when the value matches and is non-empty. */
  matchesValue?: string;
};

function MdiIcon({ path, className }: { path: string; className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="1.15em" height="1.15em" aria-hidden focusable="false">
      <path fill="currentColor" d={path} />
    </svg>
  );
}

/** Registration password input with visibility toggle and optional confirm-match hint. */
export function RegPasswordField({
  label,
  required,
  value,
  onChange,
  onBlur,
  autoComplete = "new-password",
  error,
  matchesValue,
}: Props) {
  const id = useId();
  const [visible, setVisible] = useState(false);
  const showMatch =
    matchesValue !== undefined && value.length > 0 && value === matchesValue;

  return (
    <div className="reg-field">
      <label htmlFor={id} className="reg-password-label">
        {typeof label === "string" ? <RegReqLabel required={required}>{label}</RegReqLabel> : label}
      </label>
      <div
        className={`reg-password-control${matchesValue !== undefined ? " reg-password-control--with-match" : ""}`}
      >
        <input
          id={id}
          type={visible ? "text" : "password"}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          className={error ? "reg-control--error" : undefined}
          aria-invalid={error ? true : undefined}
        />
        {showMatch ? (
          <span className="reg-password-match" aria-hidden="true">
            <MdiIcon path={mdiCheck} />
          </span>
        ) : null}
        <button
          type="button"
          className="reg-password-toggle"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? "Hide password" : "Show password"}
          aria-pressed={visible}
        >
          <MdiIcon path={visible ? mdiEyeOff : mdiEye} />
        </button>
      </div>
      {error ? <small className="reg-field-error">{error}</small> : null}
    </div>
  );
}
